import { useCallback } from 'react'
import { apiFetch } from '../../helpers/apiFetch'
import { resolveTraceHeaders } from '../../helpers/resolveTraceHeaders'
import type { AuthConfig } from '../../types'

export type AuthMutationsLoginArgs = {
  data: {
    clientId: string
    jwt: string
  }
}

const LOGIN_MUTATION = `mutation Login($data: AuthMutationsLoginInput!) {
  auth {
    login(data: $data) {
      refreshToken
      accessToken
    }
  }
}`

export const useGenerateAuthenticateAction = (config: AuthConfig) => {
  const { setRefreshToken, setAccessToken, apiUrl, getTraceHeaders } = config

  return useCallback(
    async (variables: AuthMutationsLoginArgs) => {
      const headers = await resolveTraceHeaders(getTraceHeaders)
      const response = await apiFetch(
        apiUrl,
        {
          operationName: 'Login',
          variables,
          query: LOGIN_MUTATION,
        },
        headers,
      )

      const { errors } = response
      if (errors?.length) {
        const hasForbiddenError = errors.some(
          (err) => err.extensions?.code === 'FORBIDDEN',
        )
        // This either means the user doesn't exist or is inactive, so empty all state
        if (hasForbiddenError) {
          setRefreshToken(null)
          setAccessToken(null)
        }
        throw new Error(errors.map((err) => err.message).join('\n'))
      }

      const tokens = response.data?.auth?.login
      if (!tokens) {
        throw new Error('Error logging in, empty token')
      }
      setRefreshToken(tokens.refreshToken || null)
      const accessToken: string | null = tokens.accessToken || null
      setAccessToken(accessToken)
      return accessToken
    },
    [setRefreshToken, setAccessToken, apiUrl, getTraceHeaders],
  )
}
